import React from 'react';
import { Shape } from 'react-konva';
import Konva from 'konva';
import { useAppSelector } from '../../hooks/redux';
import { aciToHex } from '../../utils/dxf';
import { isShortGridFloorLabel } from '../../utils/floorPlanLabelRender';

/**
 * Renders imported DXF TEXT labels (equipment names, grid refs, annotations) as
 * a single non-interactive Konva.Shape, mirroring FloorPlan's performance model.
 *
 * Labels are drawn in world space so they scale with zoom like real CAD text:
 *   - too small to read (on-screen cap height < MIN_LABEL_PX) → culled
 *   - off-screen (insertion point outside the viewport + margin) → culled
 *   - on a hidden DXF layer → culled
 */

/** Below this on-screen cap height (px) labels are unreadable, so we skip them. */
const MIN_LABEL_PX = 6;

const FloorPlanLabels: React.FC = () => {
  const labels = useAppSelector((s) => s.floorplan.labels);
  const layers = useAppSelector((s) => s.floorplan.layers);
  const visible = useAppSelector((s) => s.floorplan.visible);
  const showLabels = useAppSelector((s) => s.floorplan.showLabels);
  const hoveredLayerIndex = useAppSelector((s) => s.floorplan.hoveredLayerIndex);
  const opacity = useAppSelector((s) => s.floorplan.opacity);
  // Light mode only.
  const defaultStroke = '#334155';

  // Resolve each label's fill once: entity color → layer color → theme default.
  const labelColors = React.useMemo(
    () =>
      labels.map((l) => {
        const own = aciToHex(l.color);
        if (own) return own;
        const layer = layers[l.layerIndex];
        return (layer ? aciToHex(layer.color) : null) ?? defaultStroke;
      }),
    [labels, layers, defaultStroke]
  );

  const sceneFunc = React.useCallback(
    (context: Konva.Context, shape: Konva.Shape) => {
      if (labels.length === 0) return;
      const stage = shape.getStage();
      if (!stage) return;

      const ctx = context._context; // native CanvasRenderingContext2D
      const scale = stage.scaleX() || 1;
      const pos = stage.position();

      const viewLeft = -pos.x / scale;
      const viewTop = -pos.y / scale;
      const viewRight = viewLeft + stage.width() / scale;
      const viewBottom = viewTop + stage.height() / scale;

      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';

      const hasHovered = hoveredLayerIndex !== null && hoveredLayerIndex >= 0 && hoveredLayerIndex < layers.length;

      for (let i = 0; i < labels.length; i++) {
        const label = labels[i];
        const layer = layers[label.layerIndex];
        if (layer && layer.visible === false) continue;
        if (isShortGridFloorLabel(label.text)) continue;

        const h = label.height;
        if (h <= 0) continue;
        if (h * scale < MIN_LABEL_PX) continue; // too small to read

        // Cull labels whose insertion point is well outside the viewport.
        const widthWorld = label.text.length * h * 0.7;
        if (
          label.x + widthWorld < viewLeft ||
          label.x - h > viewRight ||
          label.y + h < viewTop ||
          label.y - h > viewBottom
        ) {
          continue;
        }

        const isHovered = hasHovered && label.layerIndex === hoveredLayerIndex;

        ctx.save();
        ctx.translate(label.x, label.y);
        if (label.rotation) ctx.rotate((label.rotation * Math.PI) / 180);
        ctx.font = `${isHovered ? 'bold ' : ''}${h}px sans-serif`;
        ctx.fillStyle = labelColors[i];
        ctx.globalAlpha = hasHovered ? (isHovered ? 1 : opacity * 0.25) : opacity;
        ctx.fillText(label.text, 0, 0);
        ctx.restore();
      }
    },
    [labels, layers, labelColors, hoveredLayerIndex, opacity]
  );

  if (!visible || !showLabels || labels.length === 0) return null;

  return <Shape sceneFunc={sceneFunc} listening={false} perfectDrawEnabled={false} />;
};

export default React.memo(FloorPlanLabels);
